import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LinkIcon from "@mui/icons-material/Link";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { Box, ButtonBase, Typography } from "@mui/material";
import React from "react";
import { assistedIconBadgeSx, indigoSkyAssistedIconBadgeSx } from "../assistedIconBadgeSx";
import {
  getRecipeAssistedStartCardConfigs,
  type RecipeAssistedEntryActions,
  type RecipeAssistedStartCardConfig,
} from "./recipeAssistedEntry";

export type RecipeAssistedStartCardsProps = RecipeAssistedEntryActions;

type StartCardProps = {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  /** Soft info wash for link/photo; indigo→sky for generate. */
  badgeVariant: "softInfo" | "indigoSky";
};

const StartCard = ({ title, description, icon, onClick, badgeVariant }: StartCardProps) => (
  <ButtonBase
    onClick={onClick}
    focusRipple
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      textAlign: "left",
      gap: 1.5,
      p: 2,
      height: "100%",
      minHeight: 120,
      minWidth: 0,
      width: "100%",
      border: 1,
      borderColor: "greyscale.300",
      borderRadius: 1,
      bgcolor: "common.white",
      "&:hover": {
        bgcolor: "info.lighter",
        borderColor: "text.primary",
        // Link/Photo: flip to indigo→sky. Generate: keep it (soft wash would fade into the card).
        "& [data-icon-badge]": {
          ...indigoSkyAssistedIconBadgeSx,
          boxShadow: "0 1px 4px rgba(76, 63, 184, 0.35)",
        },
      },
      "&:focus-visible": {
        outline: "2px solid",
        outlineColor: "info.main",
        outlineOffset: 2,
      },
    }}
  >
    <Box
      data-icon-badge
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: 1,
        ...assistedIconBadgeSx(badgeVariant),
        transition: theme =>
          theme.transitions.create(["background-color", "background-image", "color"], {
            duration: theme.transitions.duration.shorter,
          }),
      }}
      aria-hidden
    >
      {icon}
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", minWidth: 0 }}>
      {/* Reserve 2 lines so titles/descriptions stay level across cards when copy wraps. */}
      <Typography
        variant="body1Semibold"
        sx={{
          width: "100%",
          minHeight: "2.75rem",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          width: "100%",
          minHeight: "2.5rem",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {description}
      </Typography>
    </Box>
  </ButtonBase>
);

const cardIcon = (key: RecipeAssistedStartCardConfig["key"]) => {
  switch (key) {
    case "link":
      return <LinkIcon fontSize="small" />;
    case "photo":
      return <PhotoCameraOutlinedIcon fontSize="small" />;
    case "ai":
      return <AutoAwesomeIcon fontSize="small" />;
  }
};

export const RecipeAssistedStartCards = (props: RecipeAssistedStartCardsProps) => {
  const cards = getRecipeAssistedStartCardConfigs(props);

  if (cards.length === 0) {
    return null;
  }

  return (
    <Box
      role="group"
      aria-label="Assisted recipe creation"
      sx={{
        width: "100%",
        display: "grid",
        gap: 2,
        alignItems: "stretch",
        // Keep 3-up when the form column is squeezed (LHS open); only stack on tiny viewports.
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(3, minmax(0, 1fr))",
        },
      }}
    >
      {cards.map(card => (
        <StartCard
          key={card.key}
          title={card.title}
          description={card.description}
          icon={cardIcon(card.key)}
          onClick={card.onClick}
          badgeVariant={card.key === "ai" ? "indigoSky" : "softInfo"}
        />
      ))}
    </Box>
  );
};
